export function getDiscountedPrice(amount: number, discountPercentage: number) {
  if (typeof amount !== 'number' || typeof discountPercentage !== 'number') {
    throw new Error('Both amount and discountPercentage must be numbers');
  }

  const discount = (amount * discountPercentage) / 100;
  const finalPrice = amount - discount;

  return parseFloat(finalPrice.toFixed(2)); // rounding to 2 decimal places
}

export const getParsedData = (data: string) => {
  try {
    // First try direct parsing
    try {
      return JSON.parse(data);
    } catch (e) {
      // If direct parsing fails, try cleaning the data
      let clean = data;

      // Remove carriage returns and newlines while preserving the data structure
      clean = clean.replace(/[\r\n]\s*/g, '');

      // Remove trailing commas
      clean = clean.replace(/,\s*([\]}])/g, '$1');

      // Replace single quotes with double quotes
      clean = clean.replace(/'/g, '"');

      // Add quotes to unquoted property names
      clean = clean.replace(/(\w+):/g, '"$1":');

      // Try parsing the cleaned data
      const obj = JSON.parse(clean);
      return obj;
    }
  } catch (error) {
    console.error('Error parsing data:', error);
    return null;
  }
};

// Function to determine SQL type based on value
export const getSqlType = (value: any): string => {
  if (value === null || value === undefined) return 'TEXT';

  if (typeof value === 'number') {
    // Check if it's an integer
    if (Number.isInteger(value)) {
      if (value >= -2147483648 && value <= 2147483647) {
        return 'INTEGER';
      }
      return 'BIGINT';
    }
    return 'REAL';
  }

  if (typeof value === 'boolean') return 'BOOLEAN';

  // Check if string is a date
  if (typeof value === 'string') {
    // ISO date format check
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return 'DATE';
    }
    // Timestamp format check
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value)) {
      return 'TIMESTAMP';
    }
  }

  return 'TEXT';
};

export const getTableData = (obj: any) => {
  try {
    // Transform the data into the required format
    const tables = obj?.xlsx?.map((table: any) => {
      // Extract table name without extension
      const name = table?.name?.split('.')[0];
      console.log('Table name ===========>', name);

      // Get the first row of data to determine types
      const firstRow = table.data[0];

      // Create columns array with types
      const columns = table.columns.map((col: any, index: number) => ({
        name: col.name,
        type: col.type || getSqlType(firstRow[index]),
        autoIncrement: col.autoIncrement || false,
      }));

      return {
        name,
        columns,
        data: table.data,
      };
    });

    if (!tables) {
      return {
        tables: [],
      };
    }
    return {
      tables,
    };
  } catch (error) {
    console.error('Error parsing table data:', error);
    return {
      tables: [],
    };
  }
};

/**
 * Normalizes different types of quotation marks to standard straight quotes
 * This helps with consistent string comparison when dealing with typing exercises
 * @param text - The text to normalize
 * @returns Normalized text with standard quotes
 */
export const normalizeQuotes = (text: string): string => {
  if (typeof text !== 'string') return text;

  return (
    text
      // Replace curly double quotes with straight double quotes
      .replace(/[“”"]/g, '"')
      // Replace curly single quotes with straight single quotes
      .replace(/[‘’']/g, "'")
      // Replace smart quotes and other variations
      .replace(/['’'`’]/g, "'")
      .replace(/[""`’]/g, '"')
  );
};
